import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import PanelTemplate from '../../templates/Panel';
import Button from '../../components/Button';
import Icon from '../../components/Icon';
import Modal from '../../components/Modal';
import { Loading } from '../../components/Loading';
import api from '../../services/api';
import { renderError } from '../../helpers/errors';
import PanelIntro from './PanelIntro';
import DocsBanner from './DocsBanner';
import { SupplierApiArt } from './IntroArt';

/** Relativos a REACT_APP_URL (ex.: …/api/supplier) */
const GET_INTEGRATION_API_KEY = '/integration/api-key';
const POST_REGENERATE_INTEGRATION_API_KEY = '/integration/api-key/regenerate';

/** Scramble expõe a doc do fornecedor em <api_domain>/supplier/docs */
const DOCS_URL = 'https://api.brinde.me/supplier/docs#/';
const DOCS_LABEL = 'api.brinde.me/supplier/docs';

const integrationPayload = (res) => res.data?.data ?? res.data;

export const ApiPage = () => {
    const [loadingInitial, setLoadingInitial] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [hasKey, setHasKey] = useState(false);
    const [keyPreview, setKeyPreview] = useState('');
    const [plaintextKey, setPlaintextKey] = useState('');
    const [copied, setCopied] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);

    const displayValue = plaintextKey || keyPreview || '';
    const canCopy = Boolean(String(displayValue).trim());

    const loadKey = () => {
        setLoadingInitial(true);
        api.get(GET_INTEGRATION_API_KEY)
            .then((res) => {
                const d = integrationPayload(res);
                const hk = Boolean(d?.has_key);
                setHasKey(hk);
                setKeyPreview(typeof d?.key_preview === 'string' ? d.key_preview : '');
            })
            .catch(renderError)
            .finally(() => setLoadingInitial(false));
    };

    useEffect(() => {
        loadKey();
    }, []);

    const generateKey = () => {
        const wasRegen = hasKey;
        setGenerating(true);
        api.post(POST_REGENERATE_INTEGRATION_API_KEY)
            .then((res) => {
                const d = integrationPayload(res);
                const nextPlain = d?.api_key ?? '';
                const nextPreview = typeof d?.key_preview === 'string' ? d.key_preview : '';
                if (nextPlain) {
                    setPlaintextKey(nextPlain);
                }
                if (nextPreview) {
                    setKeyPreview(nextPreview);
                }
                setHasKey(true);
                toast.success(
                    wasRegen
                        ? 'API Key regenerada. Guarde a chave completa — ela não será exibida inteira novamente.'
                        : 'Chave gerada com sucesso. Guarde a chave completa — ela não será exibida inteira novamente.'
                );
            })
            .catch(renderError)
            .finally(() => setGenerating(false));
    };

    const handleClickGenerate = () => {
        if (hasKey) {
            setShowConfirm(true);
        } else {
            generateKey();
        }
    };

    const handleConfirmGenerate = () => {
        setShowConfirm(false);
        generateKey();
    };

    const copyToClipboard = (text, setFlag) => {
        const value = String(text ?? '').trim();
        if (!value) return;
        navigator.clipboard.writeText(value).then(() => {
            setFlag(true);
            toast.success('Copiado!');
            setTimeout(() => setFlag(false), 2000);
        });
    };

    const handleCopy = () => copyToClipboard(displayValue, setCopied);

    if (loadingInitial) {
        return (
            <PanelTemplate id="api">
                <Loading show />
            </PanelTemplate>
        );
    }

    return (
        <>
            <Modal
                title="Atenção"
                show={showConfirm}
                close={() => setShowConfirm(false)}
                footer={
                    <>
                        <Button
                            type="secondary"
                            size="small"
                            text="Cancelar"
                            action={() => setShowConfirm(false)}
                        />
                        <Button
                            type="primary"
                            size="small"
                            text="Gerar nova chave"
                            action={handleConfirmGenerate}
                        />
                    </>
                }
            >
                <div className="section">
                    <div className="api-confirm-warning">
                        <p>
                            Já existe uma chave de API cadastrada. Ao gerar uma nova, a chave atual será{' '}
                            <strong>revogada permanentemente</strong>.
                        </p>
                        <p>
                            <strong>Atualize sua aplicação</strong> com a nova chave para evitar interrupções no serviço.
                        </p>
                    </div>
                </div>
            </Modal>

            <PanelTemplate id="api">
                <div className="api-panel">
                    <DocsBanner url={DOCS_URL} label={DOCS_LABEL} />

                    <div className="panel-card">
                        <h2>Chave de API</h2>
                        <p className="panel-card__sub">
                            A chave identifica a sua empresa em cada chamada. Envie-a no header{' '}
                            <code className="inline-code">X-API-Key</code> de todas as requisições e
                            guarde-a como uma senha — quem tem a chave pode alterar o seu catálogo.
                        </p>

                        <div className="api-field">
                            <div className="keybox">
                                <span
                                    className={`keybox__value${canCopy ? '' : ' empty'}`}
                                    onClick={canCopy ? handleCopy : undefined}
                                    title={canCopy ? 'Clique para copiar' : ''}
                                >
                                    {displayValue || 'Nenhuma chave gerada'}
                                </span>
                                <button
                                    type="button"
                                    className={`keybox__copy${copied ? ' copied' : ''}`}
                                    onClick={handleCopy}
                                    disabled={!canCopy}
                                    title="Copiar"
                                >
                                    <Icon name={copied ? 'check' : 'copy'} size={16} />
                                </button>
                            </div>
                            <Button
                                type="primary"
                                size="small"
                                text={hasKey ? 'Regerar chave' : 'Gerar chave'}
                                svg={hasKey ? 'refresh' : 'api'}
                                action={handleClickGenerate}
                                loading={generating}
                            />
                        </div>

                        {plaintextKey &&
                            <p className="api-hint">
                                <Icon name="alert" size={15} strokeWidth={1.8} />
                                <span>
                                    Esta é a única vez que a chave completa aparece. Copie e guarde
                                    agora — depois só o final dela fica visível.
                                </span>
                            </p>
                        }
                    </div>

                    <PanelIntro
                        title="O que é a API?"
                        icon={<Icon name="api" size={20} strokeWidth={1.8} />}
                        iconBg="#E6F1FB"
                        iconFg="#185FA5"
                        art={<SupplierApiArt />}
                    >
                        <p>
                            A API é a ponte entre o sistema que a sua empresa já usa (ERP, PDM,
                            planilha de catálogo) e a Brinde.me. Com ela, o seu sistema{' '}
                            <strong>cadastra e atualiza produtos, variações, cores, categorias e
                            imagens</strong> automaticamente — sem ninguém precisar abrir este painel
                            ou digitar nada.
                        </p>
                        <p>
                            É o caminho recomendado quando o seu catálogo é grande ou muda com
                            frequência: preço e estoque chegam às lojas no mesmo dia em que mudam
                            no seu sistema.
                        </p>
                    </PanelIntro>
                </div>
            </PanelTemplate>
        </>
    );
};

export default ApiPage;
